/*
 * MX SERVICE FORM - behaviour of sections/mx-service-form.liquid
 *
 * A vanilla rewrite of the jQuery script of the Shoptet "Reklamace a vratky" form, with the same rules:
 *
 * - steps 2 to 4 appear once a complaint type is chosen; changing the type resets the form and drops the extra
 *   product rows
 * - each type shows its own set of fields (TYPES below) and only visible fields are required; hidden fields are
 *   still submitted, exactly as on Shoptet, because the backend may expect them
 * - "Podložka po době životnosti" only offers the pads (codes containing a D)
 * - extra product rows come from a template, complaintProducts[N] with an increasing N
 * - leaving a serial number that starts with 00024000 asks Helios (through the store's app proxy) for the age of
 *   the pad and shows the matching banner
 * - ?oc= / ?fn= ... prefill the fields, ?type= selects a type
 * - submitting sends the form as JSON (buildComplaint) to the createComplaint function behind the store's app
 *   proxy, which validates it and builds the Helios request of the chosen type;
 *   the answer is shown in the success or error banner instead of the page reloading
 * - every filled-in form carries a submissionId, kept until it is sent successfully, so that sending it again after
 *   an error (e.g. a Helios timeout that still created the complaint) does not create a second complaint
 * - the attachment must be a JPG, PNG or PDF of at most 5 MB, checked when it is picked
 */

const TYPES = {
  RECLAMATION: {
    show: ['code', 'serial', 'defect', 'description', 'add', 'step4', 'invoice', 'file'],
    required: ['code', 'defect', 'description']
  },
  RECLAMATION_PAD: {
    show: ['code', 'serial'],
    required: ['code'],
    padsOnly: true
  },
  RETURN_14: {
    show: ['returnProduct', 'add', 'step4', 'invoice', 'bank', 'file'],
    required: ['returnProduct', 'invoice', 'bank']
  },
  RETURN: {
    show: ['reason', 'code', 'serial', 'description', 'add', 'step4', 'invoice', 'bank', 'file'],
    required: ['reason', 'code', 'description', 'invoice', 'bank']
  }
};

// URL parameter -> field, as generated by the "Odstoupit od smlouvy" links of the former store.
const PREFILL = {
  oc: 'invoiceNumber',
  fn: 'firstName',
  ln: 'lastName',
  st: 'street',
  zp: 'zip',
  ct: 'city',
  em: 'email',
  ph: 'phone'
};

const SERIAL_PREFIX = '00024000';

// The base64 of the attachment travels in the JSON body, which the createComplaint function caps at 8 MiB.
const MAX_FILE_BYTES = 5 * 1024 * 1024;
const FILE_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];

// Helios check-age answer -> data-mx-age banner. 200 carries data.code (0 green, 1 yellow, 2 red); 404 (unknown
// serial) and 422 (not 14 digits) get the "check the serial number" banner.
const AGE_BY_CODE = ['validate-green', 'validate-orange', 'validate-red old'];
const AGE_SERIAL_ERROR = 'validate-red error';

class MxServiceForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.typeSelect = this.form.elements['complaint_new[type]'];
    this.steps = this.querySelector('[data-mx-steps]');
    this.products = this.querySelector('[data-mx-products]');
    this.template = this.querySelector('[data-mx-product-template]');
    this.submitButton = this.form.querySelector('button[type="submit"]');
    this.counter = this.products.children.length;
    this.params = new URLSearchParams(window.location.search);
    this.fileInput = this.form.elements['complaint_new[imageFile][file]'];
    this.submissionId = crypto.randomUUID();

    this.typeSelect.addEventListener('change', () => this.applyType(this.typeSelect.value, true));
    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.form.addEventListener('submit', this.onSubmit.bind(this));
    this.form.addEventListener('reset', () => this.fileInput?.setCustomValidity(''));
    this.fileInput?.addEventListener('change', this.validateFile.bind(this));

    this.applyType(this.initialType(), false);
  }

  get currentType() {
    return TYPES[this.typeSelect.value] ? this.typeSelect.value : '';
  }

  initialType() {
    const available = Array.from(this.typeSelect.options, (option) => option.value);
    const requested = (this.params.get('type') || '').toUpperCase();

    if (TYPES[requested] && available.includes(requested)) return requested;

    const fallback = this.getAttribute('default-type') || '';
    return TYPES[fallback] && available.includes(fallback) ? fallback : '';
  }

  applyType(type, reset) {
    if (reset) {
      this.form.reset();
      Array.from(this.products.children).slice(1).forEach((row) => row.remove());
      this.counter = this.products.children.length;
      this.querySelectorAll('[data-mx-age]').forEach((message) => (message.hidden = true));
    }

    this.typeSelect.value = type;
    this.steps.hidden = !TYPES[type];
    this.applyVisibility(this.form, type);
    this.prefill();
  }

  // Shows the fields of the type within scope (the whole form, or one new product row), and makes only the
  // visible ones required.
  applyVisibility(scope, type) {
    const config = TYPES[type] || { show: [], required: [] };

    scope.querySelectorAll('[data-mx-field]').forEach((wrapper) => {
      const field = wrapper.dataset.mxField;
      const visible = config.show.includes(field);

      wrapper.hidden = !visible;

      if (field === 'step4' || field === 'add') return;

      wrapper.querySelectorAll('input, select, textarea').forEach((control) => {
        control.required = visible && config.required.includes(field);
      });
    });

    scope.querySelectorAll('[data-mx-field="code"] select').forEach((select) => {
      Array.from(select.options).forEach((option) => {
        const excluded = Boolean(config.padsOnly) && !option.value.includes('D');
        option.hidden = excluded;
        option.disabled = excluded;
      });

      if (config.padsOnly && !select.value.includes('D')) {
        select.value = Array.from(select.options).find((option) => !option.disabled).value;
      }
    });
  }

  prefill() {
    Object.entries(PREFILL).forEach(([param, field]) => {
      const value = this.params.get(param);
      const control = this.form.elements[`complaint_new[${field}]`];

      if (value && control && !control.value) {
        control.value = value;
      }
    });
  }

  onClick(event) {
    if (event.target.closest('[data-mx-add-product] button')) {
      this.addProduct();
    } else if (event.target.closest('[data-mx-remove-product]')) {
      event.target.closest('[data-mx-product]').remove();
    }
  }

  addProduct() {
    const index = this.counter++;
    const fragment = document.createRange().createContextualFragment(
      this.template.innerHTML.replaceAll('__name__', index)
    );

    // The snippets build ids from the field name through | handle, which leaves the index out, so each added row
    // gets its own suffix to keep ids unique and labels pointing at their own field.
    fragment.querySelectorAll('[id]').forEach((element) => {
      const id = `${element.id}-${index}`;
      fragment.querySelectorAll(`[for="${element.id}"]`).forEach((label) => label.setAttribute('for', id));
      element.id = id;
    });

    const row = fragment.firstElementChild;
    this.applyVisibility(row, this.currentType);
    this.products.append(row);
  }

  onFocusOut(event) {
    const input = event.target;

    if (input.matches('[data-mx-field="serial"] input')) {
      this.checkAge(input);
    }
  }

  async checkAge(input) {
    const url = this.getAttribute('age-check-url');
    const row = input.closest('[data-mx-product]');
    const value = input.value.trim();

    // Hide the previous answer first, so it never stays up for a serial it was not given for.
    row.querySelectorAll('[data-mx-age]').forEach((message) => (message.hidden = true));
    delete row.dataset.mxAgeResult;

    if (!url || !value.startsWith(SERIAL_PREFIX)) return;

    try {
      const response = await fetch(`${url}?${new URLSearchParams({ sn: value })}`);
      const result = response.ok
        ? AGE_BY_CODE[(await response.json()).data?.code]
        : [404, 422].includes(response.status) && AGE_SERIAL_ERROR;

      // The serial changed while waiting: this answer is stale, the newer check will show its own.
      if (!result || input.value.trim() !== value) return;

      row.querySelector(`[data-mx-age="${CSS.escape(result)}"]`).hidden = false;
      row.dataset.mxAgeResult = result;
    } catch (error) {
      // Same as on Shoptet: without an answer (network, proxy) nothing is shown and the form stays usable.
    }
  }

  // The browser has already checked the visible required fields when this fires.
  async onSubmit(event) {
    event.preventDefault();
    this.submitButton.disabled = true;
    this.showMessage(null);

    try {
      const response = await fetch(this.form.action, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(await this.buildComplaint())
      });

      if (response.ok) {
        this.submissionId = crypto.randomUUID();
        this.applyType(this.initialType(), true);
        this.showMessage('success');
      } else {
        // A 422 lists the refused fields (by the function or Helios); a 400 says the invoice of a 14-day return was
        // already returned.
        const answer = await response.json().catch(() => ({}));
        const detail = response.status === 400 ? answer.server_message : Object.values(answer.errors ?? {}).flat();
        this.showMessage('error', [detail ?? []].flat().join(' '));
      }
    } catch (error) {
      this.showMessage('error');
    } finally {
      this.submitButton.disabled = false;
    }
  }

  // The form in the shape the createComplaint function validates; it keeps the fields of the type and builds the
  // Helios request (complaintID, ZIP and phone format, the legal text of the withdrawal reason).
  async buildComplaint() {
    const type = this.currentType;
    const value = (name) => (this.form.elements[`complaint_new[${name}]`]?.value ?? '').trim();
    const rows = Array.from(this.products.children, (row) => {
      const field = (key) => (row.querySelector(`[name$="[${key}]"]`)?.value ?? '').trim();

      return {
        code: field('code'),
        returnProduct: field('returnProduct'),
        serialNumber: field('serialNumber'),
        defect: field('type'),
        description: field('description')
      };
    });

    return {
      type,
      submissionId: this.submissionId,
      customer: {
        firstName: value('firstName'),
        lastName: value('lastName'),
        street: value('street'),
        city: value('city'),
        zip: value('zip'),
        country: value('country'),
        phone: value('phone'),
        email: value('email')
      },
      products: type === 'RECLAMATION_PAD' ? rows.slice(0, 1) : rows,
      invoiceNumber: value('invoiceNumber'),
      bankAccount: value('bankNumber'),
      withdrawalReason: value('reason'),
      files: await this.readFiles()
    };
  }

  validateFile() {
    const file = this.fileInput.files[0];
    let message = '';

    if (file && !FILE_TYPES.includes(file.type)) message = this.fileInput.dataset.mxFileWrongType;
    else if (file && file.size > MAX_FILE_BYTES) message = this.fileInput.dataset.mxFileTooLarge;

    this.fileInput.setCustomValidity(message);
    if (message) this.fileInput.reportValidity();
  }

  // The attachment as name and base64 content.
  async readFiles() {
    const file = this.form.elements['complaint_new[imageFile][file]']?.files[0];

    if (!file) return [];

    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });

    return [{ fileName: file.name, content: dataUrl.slice(dataUrl.indexOf(',') + 1) }];
  }

  // status: 'success', 'error' or null to hide both banners; detail is appended to the error text.
  showMessage(status, detail = '') {
    this.querySelectorAll('[data-mx-message]').forEach((banner) => {
      banner.hidden = banner.dataset.mxMessage !== status;
    });

    const detailElement = this.querySelector('[data-mx-message-detail]');
    if (detailElement) detailElement.textContent = detail ? ` ${detail}` : '';

    if (status) {
      this.querySelector(`[data-mx-message="${status}"]`)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }
}

if (!window.customElements.get('mx-service-form')) {
  window.customElements.define('mx-service-form', MxServiceForm);
}
